<template>
  <pop-up-window
    :show="this.$store.state.displayEditWebhook"
    :showClose="false"
    @close="close"
    class="doc-statuses-popup"
    fixed
  >
    <template #header>Edit Webhook Channel</template>
    <template #content>
      <div class="flex flex-col flex-nowrap">
        <div class="mb-3 flex">
          <label class="font-bold text-xl form-label mr-3 mb-0">Webhook:</label>
          <ToggleSlide
            class="w-min"
            @update-value="changeWebhookBool"
            :bool="webhookBool"
          />
        </div>
        <div v-if="webhookBool">
          <div class="flex">
            <p class="font-semibold text-lg">Webhooks:</p>
            <button
              @click="addWebhook"
              class="btn bg-theme-1 py-0 text-white shadow-md mx-2"
            >
              Add
            </button>
          </div>
          <span v-if="noWebhooksError" class="text-primary-3">{{
            noWebhooksError
          }}</span>
          <Accordion
            :key="item.ui.key"
            v-for="(item, index) in formData.webhookJobs"
            :class="{
              'border-primary-3': invalidWebhooks.includes(index),
              'border-gray-300': !invalidWebhooks.includes(index),
            }"
            class="mt-3 mb-2 border p-1 box"
          >
            <template v-slot:title>
              <span class="font-semibold text-lg">{{
                item.name ? item.name : "New webhook"
              }}</span>
              <span
                v-if="invalidWebhooks.includes(index)"
                class="text-primary-3"
                >Contains errors</span
              >
            </template>
            <template v-slot:content>
              <div class="flex flex-col">
                <div class="flex flex-row">
                  <div class="w-1/2 mr-6">
                    <Field
                      title="Job name:*"
                      type="text"
                      v-model.trim="item.name"
                      :disabled="!item.ui.isNew"
                      :hasError="!!errors[index]?.name"
                    />
                    <span v-if="errors[index]?.name" class="text-primary-3">{{
                      errors[index].name
                    }}</span>
                  </div>
                  <div class="w-1/2">
                    <Field
                      title="URL:*"
                      type="text"
                      placeholder="https://"
                      v-model.trim="item.url"
                      :hasError="!!errors[index]?.url"
                    />
                    <span v-if="errors[index]?.url" class="text-primary-3">{{
                      errors[index].url
                    }}</span>
                  </div>
                </div>
                <div class="flex flex-row">
                  <div class="w-1/2 mr-6 mb-3">
                    <p class="font-bold form-label">API key:</p>
                    <div class="input-group">
                      <input
                        type="text"
                        class="form-control"
                        v-model.trim="item.headers.apiKey"
                      />
                      <div
                        @click="generateKey(item)"
                        class="font-bold cursor-pointer input-group-text"
                      >
                        <font-awesome-icon icon="gears" />
                      </div>
                    </div>
                  </div>
                  <div class="w-1/2 mb-3">
                    <p class="font-bold form-label">Event types:*</p>
                    <div
                      :class="{
                        'border border-primary-3': errors[index]?.eventTypes,
                      }"
                    >
                      <DropdownSelectAll
                        :data="eventTypeOptions"
                        :selected="item.eventTypes"
                        :noSelectAll="true"
                        @update-value="updateEventTypes($event, item)"
                      />
                    </div>
                    <span
                      v-if="errors[index]?.eventTypes"
                      class="text-primary-3"
                      >{{ errors[index].eventTypes }}</span
                    >
                  </div>
                </div>
                <div class="mb-3 flex">
                  <label class="font-bold form-label mr-3 mb-0"
                    >Base64 documents:</label
                  >
                  <ToggleSlide
                    class="w-min"
                    @update-value="updatePdfFormat($event, item)"
                    :bool="item.pdfFormat === 'base64'"
                    :renew-bool="true"
                  />
                </div>
              </div>
              <div class="grid grid-rows-1 justify-items-end">
                <div>
                  <button
                    class="btn shadow-md bg-theme-1 text-white m-2"
                    @click="showConfirmDialog(item, 'delete')"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </template>
          </Accordion>
        </div>
      </div>
      <div class="grid grid-rows-1 justify-items-end">
        <div>
          <button class="btn shadow-md mr-2" @click="update">Update</button>
          <button
            class="btn shadow-md mr-2 nymus-orange color-white"
            @click="close"
          >
            Cancel
          </button>
        </div>
      </div>
    </template>
  </pop-up-window>
  <ConfirmDialog @close="onConfirm" :show="showConfirm" />
</template>

<script>
import PopUpWindow from "../PopUp.vue";
import Accordion from "../Accordion.vue";
import ToggleSlide from "../ToggleSlide.vue";
import Field from "../Field.vue";
import ConfirmDialog from "../ConfirmDialog.vue";
import DropdownSelectAll from "../DropdownSelectAll.vue";
import webhookEventTypes from "@/webhookEventTypes";
import { v4 as uuidv4 } from "uuid";

export default {
  name: "EditWebhookChannel",
  components: {
    PopUpWindow,
    Accordion,
    ToggleSlide,
    Field,
    ConfirmDialog,
    DropdownSelectAll,
  },
  data() {
    return {
      webhookBool: false,
      showConfirm: false,
      currentItem: {},
      confirmAction: "",
      submitted: false,
      errors: [],
      noWebhooksError: "",
      eventTypeOptions: webhookEventTypes,
      formData: {
        webhookJobs: [],
      },
    };
  },
  mounted() {
    this.initData();
  },
  computed: {
    activeSG() {
      return this.$store.state.activeSG;
    },
    invalidWebhooks() {
      return this.errors
        .map((error, index) => (Object.keys(error).length ? index : -1))
        .filter((index) => index > -1);
    },
  },
  watch: {
    activeSG() {
      this.initData();
    },
    // After the first update attempt, keep the errors in sync while typing
    formData: {
      deep: true,
      handler() {
        if (this.submitted) this.validation();
      },
    },
  },
  methods: {
    generateKey(item) {
      // Replacing an existing key breaks the receiver until it gets the new one
      if (item.headers.apiKey) {
        this.showConfirmDialog(item, "regenerate");
      } else {
        item.headers.apiKey = uuidv4();
      }
    },
    updateEventTypes(event, item) {
      item.eventTypes = event.map((i) => this.eventTypeOptions[i].name);
    },
    updatePdfFormat(data, item) {
      item.pdfFormat = data ? "base64" : "link";
    },
    changeWebhookBool(data) {
      this.webhookBool = data;
    },
    addWebhook() {
      this.formData.webhookJobs.push({
        name: "",
        url: "",
        ui: { key: uuidv4(), isNew: true },
        headers: {
          apiKey: "",
        },
        eventTypes: [this.eventTypeOptions[0].name],
        pdfFormat: "link",
      });
    },
    showConfirmDialog(item, action) {
      this.currentItem = item;
      this.confirmAction = action;
      this.showConfirm = true;
    },
    onConfirm(data) {
      if (data === "Yes") {
        if (this.confirmAction === "delete") {
          let index = this.formData.webhookJobs.indexOf(this.currentItem);
          if (index > -1) {
            this.formData.webhookJobs.splice(index, 1);
          }
        } else if (this.confirmAction === "regenerate") {
          this.currentItem.headers.apiKey = uuidv4();
        }
      }
      this.showConfirm = false;
    },
    /* INIT & POPUP BUTTONS */
    initData() {
      let webhook = this.$store.state.activeSG.channelsConfig.webhook;
      this.webhookBool = !!webhook;
      this.showConfirm = false;
      this.currentItem = {};
      this.confirmAction = "";
      this.submitted = false;
      this.errors = [];
      this.noWebhooksError = "";
      this.formData.webhookJobs = webhook?.webhookJobs
        ? JSON.parse(JSON.stringify(webhook.webhookJobs)).map((j) => ({
            ...j,
            ui: { key: uuidv4(), isNew: false },
            headers: { apiKey: "", ...(j.headers ?? {}) },
            eventTypes: j.eventTypes ?? [],
          }))
        : [];
    },
    close() {
      this.initData();
      this.$store.state.displayEditWebhook = false;
    },
    async update() {
      this.submitted = true;
      let hasErrors = this.validation();
      if (!hasErrors) {
        let body = {};
        if (this.webhookBool && this.formData.webhookJobs.length) {
          body.webhook = {
            webhookJobs: this.formData.webhookJobs.map(({ ui, ...job }) =>
              this.withoutEmptyApiKey(job),
            ),
          };
        }

        let payload = {
          sgId: this.$store.state.activeSG.supplierGroupId,
          edit: "webhook-settings",
          body: body,
        };
        await this.$store.dispatch("EditSupplierGroup", payload);
      }
    },
    // The API key is optional: don't send an empty key (or empty headers)
    withoutEmptyApiKey(job) {
      let { apiKey, ...headers } = job.headers;
      if (apiKey) headers.apiKey = apiKey;
      let { headers: _, ...rest } = job;
      return Object.keys(headers).length ? { ...rest, headers } : rest;
    },
    isValidUrl(value) {
      try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
      } catch {
        return false;
      }
    },
    validation() {
      if (!this.webhookBool) {
        this.errors = [];
        this.noWebhooksError = "";
        return false;
      }

      // Saving with the toggle on but no webhooks would remove the channel
      this.noWebhooksError = this.formData.webhookJobs.length
        ? ""
        : "Add at least one webhook or turn the webhook channel off";

      const names = this.formData.webhookJobs.map((item) => item.name);

      this.errors = this.formData.webhookJobs.map((item, index) => {
        let error = {};
        if (!item.name) {
          error.name = "Job name is required";
        } else if (names.indexOf(item.name) !== index) {
          error.name = "Job name is already used by another webhook";
        }
        if (!item.url) {
          error.url = "URL is required";
        } else if (!this.isValidUrl(item.url)) {
          error.url = "URL must start with http:// or https://";
        }
        if (!item.eventTypes.length) {
          error.eventTypes = "Select at least one event type";
        }
        return error;
      });

      return !!this.noWebhooksError || this.invalidWebhooks.length > 0;
    },
  },
};
</script>
